import { portalApi } from "portal/state/portalApi";
import { PredictionPoint } from "protos/veselka/prediction_point";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCategoryCollectionCustomerId } from "../CategoryCollectionCustomerContext";
import { useInfiniteScroll } from "portal/utils/hooks/useInfiniteScroll";
import { useLazyPopups } from "portal/utils/hooks/useApiPopups";

const PAGE_SIZE = 25;
const initialVisibleCount = (idCount: number): number =>
  Math.min(PAGE_SIZE, idCount);

interface ExpandedImageState {
  index: number;
  data: PredictionPoint;
  editableCategoryId?: string;
}

export interface OrderedCategorizedImage {
  id?: string;
  data?: PredictionPoint;
  loading: boolean;
}
interface UseCategorizedImageDataReturn {
  expandedImage?: ExpandedImageState;
  setExpandedImage: React.Dispatch<
    React.SetStateAction<ExpandedImageState | undefined>
  >;
  orderedCombinedData: OrderedCategorizedImage[];
  suggestedAdditions: number; // suggestion to the user on how many images to classify
  isLoading: boolean;
  loadMoreRef: (node: HTMLDivElement | null) => void;
  hasMore: boolean;
  loadMore: () => void;
}

/**
 * Given an ordered list of chip IDs, fetches payloads in FE-sized pages, supports
 * infinite scroll, and keeps the bar stable when IDs change: category/customer
 * switches reset and refetch; otherwise only missing IDs are fetched into the
 * cache.
 */
export const useCategorizedImageData = (
  predictionPointIds: string[],
  categoryId?: string,
  suggestedMin?: number
): UseCategorizedImageDataReturn => {
  const customerId = useCategoryCollectionCustomerId();
  const [expandedImage, setExpandedImage] = useState<
    ExpandedImageState | undefined
  >();

  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const requestGenerationTokenRef = useRef(Symbol());
  const visibleIdsRef = useRef<string[]>([]);
  const [visibleCount, setVisibleCount] = useState(() =>
    initialVisibleCount(predictionPointIds.length)
  );

  const cacheRef = useRef<{
    points: { [id: string]: PredictionPoint };
    missingIds: Set<string>;
  }>({ points: {}, missingIds: new Set() });

  const prevCategoryIdRef = useRef(categoryId);
  const prevCustomerIdRef = useRef(customerId);
  const prevIdsLengthRef = useRef(predictionPointIds.length);
  const categoryChanged = categoryId !== prevCategoryIdRef.current;
  const customerChanged = customerId !== prevCustomerIdRef.current;

  if (categoryChanged || customerChanged) {
    if (categoryChanged) {
      // reset editable expanded image category if tab changes
      setExpandedImage((prev) =>
        prev ? { ...prev, editableCategoryId: categoryId } : undefined
      );
    }
    requestGenerationTokenRef.current = Symbol();
    cacheRef.current = { points: {}, missingIds: new Set() };
    isLoadingRef.current = false;
    setVisibleCount(initialVisibleCount(predictionPointIds.length));
    prevCategoryIdRef.current = categoryId;
    prevCustomerIdRef.current = customerId;
    prevIdsLengthRef.current = predictionPointIds.length;
  } else if (predictionPointIds.length !== prevIdsLengthRef.current) {
    const prevLen = prevIdsLengthRef.current;
    const nextLen = predictionPointIds.length;
    if (prevLen === 0) {
      setVisibleCount(initialVisibleCount(nextLen));
    } else if (nextLen < visibleCount) {
      setVisibleCount(nextLen);
    } else if (nextLen > prevLen && visibleCount >= prevLen) {
      setVisibleCount(nextLen);
    }
    prevIdsLengthRef.current = nextLen;
  }

  const [getPredictionPoints] = useLazyPopups(
    portalApi.endpoints.listPredictionPoints.useLazyQuery()
  );

  const visibleIds = useMemo(
    () => predictionPointIds.slice(0, visibleCount),
    [predictionPointIds, visibleCount]
  );
  visibleIdsRef.current = visibleIds;
  const hasMore = visibleCount < predictionPointIds.length;

  const determineNextPageIds = useCallback(
    (): string[] => pageOfUnresolvedIds(visibleIds, cacheRef.current),
    [visibleIds]
  );

  const fetchChipPoints = useCallback(
    async (idsToFetch: string[]) => {
      if (idsToFetch.length === 0 || isLoadingRef.current) {
        return;
      }
      const generationToken = requestGenerationTokenRef.current;
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const { data: results, isError: fetchError } =
          await getPredictionPoints({
            page: 1, // always 1 — pagination is FE chunking of the id list
            pageSize: idsToFetch.length,
            ids: idsToFetch,
            customerIds: customerId ? [customerId] : undefined, // uuid
          });

        if (generationToken !== requestGenerationTokenRef.current) {
          return;
        }

        if (results) {
          results.data.forEach((predictionPoint) => {
            cacheRef.current.points[predictionPoint.id] = predictionPoint;
          });
          idsToFetch.forEach((id) => {
            if (cacheRef.current.points[id]) {
              cacheRef.current.missingIds.delete(id);
            } else {
              cacheRef.current.missingIds.add(id);
            }
          });
        }
        if (fetchError) {
          idsToFetch.forEach((id) => {
            cacheRef.current.missingIds.add(id);
          });
        }
      } catch {
        // Leave this batch unresolved. The follow-up below only fetches ids
        // that were not in it, so a thrown request is not retried forever.
      } finally {
        if (generationToken === requestGenerationTokenRef.current) {
          // eslint-disable-next-line require-atomic-updates
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      }

      if (generationToken !== requestGenerationTokenRef.current) {
        return;
      }
      // Ids added while this request was in flight were skipped by the lock.
      // Fetch them now. Skip when every unresolved id was in this batch, so a
      // thrown request cannot retry itself forever.
      const nextIds = pageOfUnresolvedIds(
        visibleIdsRef.current,
        cacheRef.current
      );
      const attempted = new Set(idsToFetch);
      if (nextIds.some((id) => !attempted.has(id))) {
        fetchChipPoints(nextIds);
      }
    },
    [customerId, getPredictionPoints]
  );

  const loadMore = useCallback(() => {
    const idsToFetch = determineNextPageIds();
    if (idsToFetch.length > 0) {
      fetchChipPoints(idsToFetch);
      return;
    }
    setVisibleCount((current) =>
      Math.min(current + PAGE_SIZE, predictionPointIds.length)
    );
  }, [determineNextPageIds, fetchChipPoints, predictionPointIds.length]);

  useEffect(() => {
    fetchChipPoints(determineNextPageIds());
  }, [determineNextPageIds, fetchChipPoints]);

  const orderedCombinedData = buildOrderedCategorizedImages(
    visibleIds,
    cacheRef.current.points,
    cacheRef.current.missingIds
  );

  const loadMoreRef = useInfiniteScroll<HTMLDivElement>(isLoading, loadMore);
  const suggestedAdditions = determineSuggestedAdditions(
    suggestedMin ?? 0,
    predictionPointIds.length
  );

  return {
    expandedImage,
    setExpandedImage,
    orderedCombinedData,
    suggestedAdditions,
    isLoading,
    loadMoreRef,
    hasMore,
    loadMore,
  };
};

const pageOfUnresolvedIds = (
  ids: string[],
  cache: {
    points: { [id: string]: PredictionPoint };
    missingIds: Set<string>;
  }
): string[] => {
  const unresolved: string[] = [];
  for (const id of ids) {
    if (unresolved.length >= PAGE_SIZE) {
      break;
    }
    if (!cache.points[id] && !cache.missingIds.has(id)) {
      unresolved.push(id);
    }
  }
  return unresolved;
};

const determineSuggestedAdditions = (
  suggestedMin: number,
  currentCount: number
): number => (suggestedMin > currentCount ? suggestedMin - currentCount : 0);

const buildOrderedCategorizedImages = (
  ids: string[],
  cachedPredictionPoints: { [id: string]: PredictionPoint },
  missingIds?: Set<string> // ids that have failed to return when queried
): OrderedCategorizedImage[] =>
  ids.map((id) => {
    const data = cachedPredictionPoints[id];
    const isMissing = missingIds?.has(id) ?? false;
    return {
      id,
      data,
      // skeleton while unresolved (in-flight or not yet requested)
      loading: Boolean(!data && !isMissing),
    };
  });
